/**
 * MigrationView — EdgeMail Migration Tool admin tab.
 *
 * PR 5 ships the navigation shell with three sub-tabs (Jobs / Create / DLQ),
 * all rendering empty states. The full job-detail panel, wizard, and DLQ
 * triage UI land in PR 13/14.
 *
 * Pattern mirrors MailboxListView: TanStack Query for data, plain Tailwind
 * for layout, Heroicons for affordances.
 */
import React, { useState } from 'react';
import {
  ArrowUpTrayIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PlusCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useMigrationJobs } from '../../hooks/useMigrationJobs';
import type { MigrationSubTab, MigrationJob } from '../../types/migration.types';
import { CreateMigrationJobWizard } from '../wizards/CreateMigrationJobWizard';
import { MigrationJobDetailPanel } from '../panels/MigrationJobDetailPanel';
import { MigrationDlqTriagePanel } from '../panels/MigrationDlqTriagePanel';

const SUB_TABS: Array<{ id: MigrationSubTab; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { id: 'jobs', label: 'Jobs', icon: ClockIcon },
  { id: 'create', label: 'Create', icon: PlusCircleIcon },
  { id: 'dlq', label: 'Dead Letter', icon: ExclamationTriangleIcon },
];

function statusBadgeClasses(status: MigrationJob['status']): string {
  switch (status) {
    case 'completed':
      return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
    case 'running':
    case 'validating':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
    case 'paused':
    case 'awaiting_canary_approval':
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
    case 'failed':
      return 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300';
    case 'cancelled':
      return 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
    case 'queued':
    default:
      return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  }
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString();
}

// ─── Sub-views ───────────────────────────────────────────────────────────
interface JobsListProps {
  onOpenWizard: () => void;
  onSelectJob: (jobId: string) => void;
}

function JobsList({ onOpenWizard, onSelectJob }: JobsListProps) {
  const { data, isLoading, isError, refetch } = useMigrationJobs();

  if (isLoading) {
    return <SubViewSpinner label="Loading migration jobs…" />;
  }
  if (isError) {
    return (
      <div className="text-center py-12 text-rose-700 dark:text-rose-300">
        <p className="mb-3">Could not load migration jobs.</p>
        <button
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-rose-100 dark:bg-rose-900/30 text-sm font-medium hover:bg-rose-200"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Retry
        </button>
      </div>
    );
  }

  if (!data || data.total === 0) {
    return (
      <EmptyState
        icon={ClockIcon}
        title="No migration jobs yet"
        body="When you import a PST, MBOX, or live mailbox, jobs appear here with live progress, ETA, and per-folder breakdown."
        ctaLabel="Create your first migration"
        ctaOnClick={onOpenWizard}
      />
    );
  }

  return (
    <div className="overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg">
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800/60">
          <tr>
            {['Status', 'Source', 'Mailbox', 'Progress', 'DLQ', 'Last activity', 'Created'].map((h) => (
              <th
                key={h}
                scope="col"
                className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800">
          {data.jobs.map((job) => {
            const denominator = Math.max(job.total_estimated, job.processed, 1);
            const progressPct = job.total_estimated > 0 || job.processed > 0
              ? Math.min(100, Math.round((job.imported + job.deduped) / denominator * 100))
              : 0;
            const isActive = job.status === 'running' || job.status === 'validating' || job.status === 'queued';
            return (
              <tr
                key={job.job_id}
                onClick={() => onSelectJob(job.job_id)}
                className="hover:bg-gray-50 dark:hover:bg-gray-800/60 cursor-pointer"
              >
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${statusBadgeClasses(job.status)}`}
                  >
                    {isActive && <span className="inline-block h-1.5 w-1.5 rounded-full bg-current animate-pulse" />}
                    {job.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200">{job.source_type}</td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200">
                  {job.target_mailbox_email || job.target_mailbox_id}
                </td>
                <td className="px-4 py-3 text-sm tabular-nums text-gray-700 dark:text-gray-300 min-w-[220px]">
                  <div className="flex items-center justify-between mb-1">
                    <span>
                      {job.imported.toLocaleString()} / {job.total_estimated > 0 ? job.total_estimated.toLocaleString() : '—'}
                      {job.deduped > 0 && (
                        <span className="ml-1.5 text-xs text-gray-500">+{job.deduped.toLocaleString()} dedup</span>
                      )}
                    </span>
                    <span className="text-xs text-gray-500">{job.total_estimated > 0 ? `${progressPct}%` : `${job.processed.toLocaleString()} processed`}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                    <div
                      className={`h-full transition-all duration-200 ${isActive ? 'bg-lex-accent' : 'bg-emerald-500'}`}
                      style={{ width: job.total_estimated > 0 ? `${progressPct}%` : (isActive ? '100%' : '0%'), opacity: isActive && job.total_estimated === 0 ? 0.4 : 1 }}
                    />
                  </div>
                </td>
                <td className="px-4 py-3 text-sm tabular-nums">
                  {job.failed > 0 ? (
                    <span className="text-rose-600 dark:text-rose-400 font-medium">{job.failed.toLocaleString()}</span>
                  ) : (
                    <span className="text-gray-400">0</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                  {formatRelativeTime(job.updated_at)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                  {formatDate(job.created_at)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function formatRelativeTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '—';
  const deltaSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (deltaSec < 5) return 'just now';
  if (deltaSec < 60) return `${deltaSec}s ago`;
  if (deltaSec < 3600) return `${Math.floor(deltaSec / 60)}m ago`;
  if (deltaSec < 86400) return `${Math.floor(deltaSec / 3600)}h ago`;
  return `${Math.floor(deltaSec / 86400)}d ago`;
}

function CreatePanel({ onOpenWizard }: { onOpenWizard: () => void }) {
  return (
    <EmptyState
      icon={PlusCircleIcon}
      title="Start a new migration"
      body="Pick a recipe, choose a target mailbox, and review the options before any compute commits."
      ctaLabel="Open wizard"
      ctaOnClick={onOpenWizard}
    />
  );
}

function DlqPanel({ jobs }: { jobs: MigrationJob[] }) {
  const [selectedJobId, setSelectedJobId] = useState<string>('');
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Job</label>
        <select
          value={selectedJobId}
          onChange={(e) => setSelectedJobId(e.target.value)}
          className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm w-full max-w-md"
        >
          <option value="">— Select a migration job —</option>
          {jobs.map((j) => (
            <option key={j.job_id} value={j.job_id}>
              {j.source_type} • {j.target_mailbox_email || j.target_mailbox_id} • {j.status} ({j.failed} failed)
            </option>
          ))}
        </select>
      </div>
      <MigrationDlqTriagePanel jobId={selectedJobId || null} />
    </div>
  );
}

// ─── Layout pieces ───────────────────────────────────────────────────────
function SubViewSpinner({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center py-12 text-sm text-gray-500 dark:text-gray-400">
      <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
      {label}
    </div>
  );
}

interface EmptyStateProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  body: string;
  ctaLabel?: string;
  ctaOnClick?: () => void;
  ctaDisabledHint?: string;
}

function EmptyState({ icon: Icon, title, body, ctaLabel, ctaOnClick, ctaDisabledHint }: EmptyStateProps) {
  const ctaDisabled = !ctaOnClick;
  return (
    <div className="text-center py-16 px-4">
      <div className="mx-auto h-12 w-12 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
        <Icon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
      </div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1.5">{title}</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">{body}</p>
      {ctaLabel && (
        <button
          onClick={ctaOnClick}
          disabled={ctaDisabled}
          title={ctaDisabledHint}
          className={`mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium ${
            ctaDisabled
              ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
              : 'bg-lex-accent text-white hover:bg-lex-accent-strong'
          }`}
        >
          <PlusCircleIcon className="h-4 w-4" />
          {ctaLabel}
        </button>
      )}
    </div>
  );
}

// ─── Top-level shell ─────────────────────────────────────────────────────
export function MigrationView() {
  const [activeTab, setActiveTab] = useState<MigrationSubTab>('jobs');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const { data: jobsData } = useMigrationJobs();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ArrowUpTrayIcon className="h-5 w-5 text-lex-accent" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Migration</h2>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Import historical email from Outlook (PST/OST/MSG/EML), Gmail (Takeout/Workspace/IMAP), and other sources.
          </p>
        </div>
        <button
          onClick={() => setWizardOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-lex-accent text-white text-sm font-medium hover:bg-lex-accent-strong"
        >
          <PlusCircleIcon className="h-4 w-4" />
          New job
        </button>
      </div>

      {/* Sub-tab strip */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-6" aria-label="Migration sub-tabs">
          {SUB_TABS.map((tab) => {
            const isActive = tab.id === activeTab;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`group inline-flex items-center gap-2 py-2 px-1 border-b-2 text-sm font-medium ${
                  isActive
                    ? 'border-lex-accent text-lex-accent'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Sub-view content */}
      <div>
        {activeTab === 'jobs' && (
          <JobsList
            onOpenWizard={() => setWizardOpen(true)}
            onSelectJob={(id) => setSelectedJobId(id)}
          />
        )}
        {activeTab === 'create' && <CreatePanel onOpenWizard={() => setWizardOpen(true)} />}
        {activeTab === 'dlq' && <DlqPanel jobs={jobsData?.jobs ?? []} />}
      </div>

      <CreateMigrationJobWizard
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSuccess={() => setActiveTab('jobs')}
      />
      <MigrationJobDetailPanel
        jobId={selectedJobId}
        isOpen={!!selectedJobId}
        onClose={() => setSelectedJobId(null)}
      />
    </div>
  );
}

export default MigrationView;
